import { useEffect, useMemo, useRef, useState } from "react";
import { apiDelete, apiGet, apiPatch, apiPost } from "../api/client";
import type { ConfigSaveResponse } from "../lib/applyResult";
import {
  addConnectionError,
  connectionLiveness,
  addressText,
  changedFields,
  CONNECTION_TRUST_TIERS,
  CONNECTIONS_FILTER_MIN,
  connectionName,
  connectionSaveText,
  defaultPort,
  trustTierHelp,
  defaultTrustText,
  defaultTrustTier,
  duplicateAddress,
  generateConnectionId,
  hasUnsavedChanges,
  lastHeardText,
  portHelp,
  saveOutcomeText,
  settleOutcome,
  showsTrustPicker,
  META_TRUST_SENTENCE,
  sortConnections,
  statusDot,
  statusLine,
  testResultText,
  type TestResult,
} from "../lib/connections";
import { CONNECTION_FIELD_TEXT, CONNECTION_KIND_LABEL, CONNECTION_KIND_ORDER } from '../lib/definitions/settings';
import { livenessSeverity } from '../lib/definitions/health';
import { REMOVE_CONNECTION_BUTTON, removeConnectionMessage, type TransportUsage } from "../lib/removeConnection";
import { ConfirmModal } from "./AppModal";

// Settings > Connections: a purpose-built editor for the `transports` section (the generic
// dynamic-keyed editor in SettingsPage.tsx still serves every other section). Left: the list of
// connections with live status; right: the selected connection's detail. On a phone (CSS,
// 640 px) the list fills the screen and the detail / add form open as their own screens.

export interface ConnField {
  path: string;
  type?: string;
  default?: unknown;
  description?: string;
  choices?: string[];
  group?: string;
}

type Item = Record<string, unknown>;
interface Row {
  id: string;
  baseline: Item;
  current: Item;
}
interface Health {
  state: string;
  last_packet_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  last_connected_at: string | null;
  last_stats_at: string | null;
  reconnect_attempts: number | null;
  next_retry_at: string | null;
}
interface HealthResponse {
  transports?: {
    id: string;
    state: string;
    last_packet_at: string | null;
    last_error?: string | null;
    last_error_at?: string | null;
    last_connected_at?: string | null;
    last_stats_at?: string | null;
    reconnect_attempts?: number | null;
    next_retry_at?: string | null;
  }[];
}
interface Msg {
  id: string; // "" = not tied to a connection (shown above the list)
  kind: "ok" | "error";
  text: string;
}
type Screen = "list" | "detail" | "add";

const POLL_MS = 5000;
// After a save the connection is watched this long (one look per second) before the outcome is shown.
const SETTLE_LOOKS = 8;

function toHealthMap(h: HealthResponse): Record<string, Health> {
  const next: Record<string, Health> = {};
  for (const t of h.transports ?? []) {
    next[t.id] = {
      state: t.state,
      last_packet_at: t.last_packet_at,
      last_error: t.last_error ?? null,
      last_error_at: t.last_error_at ?? null,
      last_connected_at: t.last_connected_at ?? null,
      last_stats_at: t.last_stats_at ?? null,
      reconnect_attempts: t.reconnect_attempts ?? null,
      next_retry_at: t.next_retry_at ?? null,
    };
  }
  return next;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

function isOn(item: Item): boolean {
  return item.enabled !== false;
}

// Store What It Hears is on unless it is explicitly off.
function isStatsOnly(item: Item): boolean {
  return item.ingest_packets === false;
}

function textOf(v: unknown): string {
  return typeof v === "string" ? v : "";
}

// A number box that lets the operator type freely (partial values, empty) and reports a number,
// or null when allowed and emptied.
function NumBox({
  value,
  onChange,
  allowNull,
  step,
  ariaLabel,
}: {
  value: unknown;
  onChange: (v: number | null) => void;
  allowNull?: boolean;
  step?: number | "any";
  ariaLabel: string;
}) {
  const shown = typeof value === "number" && Number.isFinite(value) ? String(value) : "";
  const [text, setText] = useState(shown);
  // Follow an outside change (Clear, switching connection) but never fight what is being typed.
  useEffect(() => {
    setText((prev) => (prev.trim() !== "" && Number(prev) === value ? prev : shown));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);
  return (
    <input
      type="number"
      inputMode="decimal"
      step={step ?? "any"}
      value={text}
      aria-label={ariaLabel}
      onChange={(e) => {
        setText(e.target.value);
        const raw = e.target.value.trim();
        if (raw === "") {
          if (allowNull) onChange(null);
          return;
        }
        const n = Number(raw);
        if (Number.isFinite(n)) {
          onChange(n);
        }
      }}
      onBlur={() => setText(shown)}
    />
  );
}

export function ConnectionsPanel({
  path,
  description,
  itemFields,
  initialValue,
}: {
  path: string;
  description?: string;
  itemFields: ConnField[];
  initialValue: Record<string, Item>;
}) {
  const [rows, setRows] = useState<Row[]>(() =>
    Object.entries(initialValue).map(([id, item]) => ({ id, baseline: item, current: { ...item } })),
  );
  const sortedIds = useMemo(
    () => sortConnections(rows.map((r) => ({ id: r.id, label: r.current.label }))).map((c) => c.id),
    [rows],
  );
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    const first = sortConnections(Object.entries(initialValue).map(([id, it]) => ({ id, label: it.label })))[0];
    return first ? first.id : null;
  });
  const [screen, setScreen] = useState<Screen>("list");
  const [filterText, setFilterText] = useState("");
  const [health, setHealth] = useState<Record<string, Health>>({});
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [saveDuplicate, setSaveDuplicate] = useState<string | null>(null);
  const [msg, setMsg] = useState<Msg | null>(null);
  const [removeDialog, setRemoveDialog] = useState<{ id: string; message: string } | null>(null);
  const [leaveDialog, setLeaveDialog] = useState<{ name: string; id: string; next: () => void } | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ scope: string; result: TestResult } | null>(null);

  // Add form
  const [addName, setAddName] = useState("");
  const [addKind, setAddKind] = useState<string>("meshtastic_tcp");
  const [addHost, setAddHost] = useState("");
  const [addPort, setAddPort] = useState(String(defaultPort("meshtastic_tcp")));
  const [addPortTouched, setAddPortTouched] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addDuplicate, setAddDuplicate] = useState<string | null>(null);

  const fieldByPath = useMemo(() => new Map(itemFields.map((f) => [f.path, f])), [itemFields]);
  const fieldPaths = useMemo(() => itemFields.map((f) => f.path), [itemFields]);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Live status: /api/health every few seconds while the panel is open.
  const refreshHealth = useRef<() => void>(() => undefined);
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const h = await apiGet<HealthResponse>("/api/health");
        if (cancelled) return;
        setHealth(toHealthMap(h));
        setNow(Date.now());
      } catch {
        /* keep the last known status */
      }
    }
    refreshHealth.current = () => void load();
    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const selected = rows.find((r) => r.id === selectedId) ?? null;
  const dirty = selected ? hasUnsavedChanges(selected.baseline, selected.current) : false;
  const showFilter = rows.length >= CONNECTIONS_FILTER_MIN;

  const visibleIds = sortedIds.filter((id) => {
    if (!showFilter || !filterText.trim()) return true;
    const r = rows.find((x) => x.id === id);
    if (!r) return false;
    const q = filterText.trim().toLowerCase();
    return (
      connectionName({ id, label: r.current.label }).toLowerCase().includes(q) ||
      id.toLowerCase().includes(q) ||
      addressText(r.current.host, r.current.port).toLowerCase().includes(q)
    );
  });

  // Leaving a connection with unsaved changes asks once, in the app's own modal; confirming
  // drops the edits and carries on with `next`.
  function guardLeave(next: () => void) {
    if (!selected || !dirty) {
      next();
      return;
    }
    const id = selected.id;
    setLeaveDialog({
      id,
      name: connectionName({ id, label: selected.current.label }),
      next: () => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, current: { ...r.baseline } } : r)));
        next();
      },
    });
  }

  function selectRow(id: string) {
    if (id === selectedId) {
      setScreen("detail");
      setMsg(null);
      return;
    }
    guardLeave(() => {
      setSelectedId(id);
      setSaveDuplicate(null);
      setScreen("detail");
      setMsg(null);
      setTestResult(null);
    });
  }

  function backToList() {
    guardLeave(() => setScreen("list"));
  }

  function openAdd() {
    guardLeave(() => {
      setAddError(null);
      setAddDuplicate(null);
      setScreen("add");
      setMsg(null);
    });
  }

  function setField(id: string, key: string, value: unknown) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, current: { ...r.current, [key]: value } } : r)));
    setMsg((m) => (m && m.id === id ? null : m));
    if (key === "host" || key === "port") {
      setTestResult(null);
      setSaveDuplicate(null);
    }
  }

  async function patchOne(id: string, body: Item): Promise<ConfigSaveResponse> {
    return apiPatch<ConfigSaveResponse>(`/api/config/${encodeURIComponent(path)}`, { patch: { [id]: body } });
  }

  // After a save of a connection that is on: look at /api/health once a second for up to 8 s and
  // say how it went. The backend's own "could not apply" answer wins and needs no waiting.
  async function reportOutcome(id: string, resp: ConfigSaveResponse, on: boolean) {
    const base = connectionSaveText(resp?.applied, id);
    if (!on || !base.startsWith("Saved and")) {
      setMsg({ id, kind: base.startsWith("Saved and") ? "ok" : "error", text: base });
      refreshHealth.current();
      return;
    }
    setMsg({ id, kind: "ok", text: "Saved. Waiting for the connection…" });
    let latest: Health | undefined;
    for (let i = 0; i < SETTLE_LOOKS; i += 1) {
      await sleep(1000);
      if (!mounted.current) return;
      try {
        const h = toHealthMap(await apiGet<HealthResponse>("/api/health"));
        latest = h[id];
        setHealth(h);
        setNow(Date.now());
      } catch {
        /* try again on the next look */
      }
      if (latest?.state === "connected") break;
    }
    if (!mounted.current) return;
    const outcome = settleOutcome(latest);
    setMsg({ id, kind: outcome.state === "failed" ? "error" : "ok", text: saveOutcomeText(outcome) });
  }

  async function onToggle(next: boolean) {
    if (!selected || busy || next === isOn(selected.current)) return;
    const id = selected.id;
    setBusy(true);
    setMsg(null);
    try {
      const resp = await patchOne(id, { enabled: next });
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { baseline: { ...r.baseline, enabled: next }, id, current: { ...r.current, enabled: next } } : r)),
      );
      setBusy(false);
      await reportOutcome(id, resp, next);
    } catch (e) {
      setMsg({ id, kind: "error", text: e instanceof Error ? e.message : "Save failed." });
    } finally {
      setBusy(false);
    }
  }

  // `force` = the operator chose Save Anyway after the same-address warning.
  async function onSave(force = false) {
    if (!selected || busy || !dirty) return;
    const id = selected.id;
    const body = changedFields(fieldPaths, selected.baseline, selected.current);
    if (Object.keys(body).length === 0) return;
    if (!force && ("host" in body || "port" in body)) {
      const dup = duplicateAddress(
        rows.map((x) => ({ id: x.id, label: x.current.label, host: x.current.host, port: x.current.port })),
        textOf(selected.current.host),
        Number(selected.current.port),
        id,
      );
      if (dup) {
        setSaveDuplicate(dup);
        return;
      }
    }
    setSaveDuplicate(null);
    setBusy(true);
    setMsg(null);
    try {
      const resp = await patchOne(id, body);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, baseline: { ...r.current } } : r)));
      setBusy(false);
      await reportOutcome(id, resp, isOn(selected.current));
    } catch (e) {
      setMsg({ id, kind: "error", text: e instanceof Error ? e.message : "Save failed." });
    } finally {
      setBusy(false);
    }
  }

  async function runTest(scope: string, kind: string, host: string, port: number) {
    if (testing) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await apiPost<TestResult>("/api/config/transports/test", { kind, host: host.trim(), port });
      setTestResult({ scope, result });
    } catch (e) {
      setTestResult({ scope, result: { ok: false, detail: e instanceof Error ? e.message : "The test could not run." } });
    } finally {
      setTesting(false);
    }
  }

  async function onRemoveClick() {
    if (!selected || busy) return;
    const id = selected.id;
    setMsg(null);
    try {
      const usage = await apiGet<TransportUsage>(`/api/config/transports/${encodeURIComponent(id)}/usage`);
      setRemoveDialog({ id, message: removeConnectionMessage(connectionName({ id, label: selected.current.label }), usage) });
    } catch (e) {
      setMsg({ id, kind: "error", text: e instanceof Error ? e.message : "Could not read what this connection stored." });
    }
  }

  async function doTurnOffInstead(id: string) {
    setBusy(true);
    setMsg(null);
    try {
      const resp = await patchOne(id, { enabled: false });
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { baseline: { ...r.baseline, enabled: false }, id, current: { ...r.current, enabled: false } } : r)),
      );
      const text = connectionSaveText(resp?.applied, id);
      setMsg({ id, kind: text.startsWith("Saved and") ? "ok" : "error", text: text.startsWith("Saved and") ? "Turned off." : text });
      refreshHealth.current();
    } catch (e) {
      setMsg({ id, kind: "error", text: e instanceof Error ? e.message : "Save failed." });
    } finally {
      setBusy(false);
    }
  }

  async function doRemove(id: string) {
    const row = rows.find((r) => r.id === id);
    const name = row ? connectionName({ id, label: row.current.label }) : id;
    setBusy(true);
    setMsg(null);
    try {
      const resp = await apiDelete<ConfigSaveResponse>(`/api/config/${encodeURIComponent(path)}/${encodeURIComponent(id)}`);
      const remaining = sortedIds.filter((x) => x !== id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      setSelectedId(remaining[0] ?? null);
      setScreen("list");
      const bad = (resp?.applied ?? []).filter((a) => a.result === "needs_attention").map((a) => a.detail || a.path);
      setMsg({ id: "", kind: bad.length ? "error" : "ok", text: bad.length ? `Removed ${name}. ${bad.join(" ")}` : `Removed ${name}.` });
    } catch (e) {
      setMsg({ id, kind: "error", text: e instanceof Error ? e.message : "Remove failed." });
    } finally {
      setBusy(false);
    }
  }

  const takenIds = rows.map((r) => r.id);

  // `force` = the operator chose Save Anyway after the same-address warning.
  async function onAdd(force: boolean) {
    const err = addConnectionError(addName, addHost, addPort);
    if (err) {
      setAddError(err);
      return;
    }
    if (!force) {
      const dup = duplicateAddress(
        rows.map((r) => ({ id: r.id, label: r.current.label, host: r.current.host, port: r.current.port })),
        addHost,
        Number(addPort),
      );
      if (dup) {
        setAddDuplicate(dup);
        return;
      }
    }
    setBusy(true);
    setAddError(null);
    setAddDuplicate(null);
    const id = generateConnectionId(addName, takenIds);
    // Everything not asked for in the form is the schema's own default.
    const item: Item = {};
    for (const f of itemFields) if (f.default !== null && f.default !== undefined) item[f.path] = f.default;
    item.enabled = true;
    item.kind = addKind;
    item.trust_tier = defaultTrustTier(addKind);
    item.label = addName.trim();
    item.host = addHost.trim();
    item.port = Number(addPort);
    try {
      const resp = await patchOne(id, item);
      setRows((prev) => [...prev, { id, baseline: item, current: { ...item } }]);
      setSelectedId(id);
      setSaveDuplicate(null);
      setScreen("detail");
      setAddName("");
      setAddHost("");
      setAddKind("meshtastic_tcp");
      setAddPort(String(defaultPort("meshtastic_tcp")));
      setAddPortTouched(false);
      setTestResult(null);
      setBusy(false);
      await reportOutcome(id, resp, true);
    } catch (e) {
      setAddError(e instanceof Error ? e.message : "Could not add the connection.");
    } finally {
      setBusy(false);
    }
  }

  function textFor(p: string) {
    return CONNECTION_FIELD_TEXT[p] ?? { label: p, help: fieldByPath.get(p)?.description ?? "" };
  }

  // One schema-driven field of the Advanced fold.
  function renderAdvanced(f: ConnField, r: Row) {
    const t = textFor(f.path);
    const value = r.current[f.path] ?? f.default;
    // Read Radio Stats: a plain checkbox over the stored companion / none choice.
    if (f.path === "stats_source") {
      return (
        <div className="conn-field" key={f.path}>
          <label className="conn-check">
            <input
              type="checkbox"
              checked={value === "companion"}
              onChange={(e) => setField(r.id, f.path, e.target.checked ? "companion" : "none")}
            />
            <span>{t.label}</span>
          </label>
          <span className="conn-help">{t.help}</span>
        </div>
      );
    }
    let control: JSX.Element;
    if (f.choices && f.choices.length > 0) {
      control = (
        <select value={value != null ? String(value) : ""} onChange={(e) => setField(r.id, f.path, e.target.value)}>
          {f.choices.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      );
    } else if (f.type === "bool") {
      control = (
        <label className="conn-check">
          <input type="checkbox" checked={value !== false} onChange={(e) => setField(r.id, f.path, e.target.checked)} />
          <span>{t.label}</span>
        </label>
      );
    } else if (f.type === "int" || f.type === "float") {
      control = <NumBox value={value} ariaLabel={t.label} step={f.type === "int" ? 1 : "any"} onChange={(n) => setField(r.id, f.path, n)} />;
    } else {
      control = <input type="text" value={textOf(value)} onChange={(e) => setField(r.id, f.path, e.target.value)} />;
    }
    return (
      <div className="conn-field" key={f.path}>
        {f.type !== "bool" && <span className="conn-label">{t.label}</span>}
        {control}
        <span className="conn-help">{t.help}</span>
      </div>
    );
  }

  // Store What It Hears has its own place under What We Trust It For, so it is not in the fold;
  // Stats Every only shows while Read Radio Stats is ticked.
  const advancedFields = (r: Row) =>
    itemFields.filter((f) => {
      if (f.path === "ingest_packets") return false;
      if (f.path === "stats_poll_interval_s" && r.current.stats_source !== "companion") return false;
      return f.group === "advanced" || (f.group === "advanced_meshcore" && r.current.kind === "meshcore_tcp");
    });

  function renderTest(scope: string, kind: string, host: string, port: number) {
    const mine = testResult && testResult.scope === scope ? testResult.result : null;
    const canTest = host.trim() !== "" && Number.isInteger(port) && port >= 1 && port <= 65535 && kind !== "";
    return (
      <div className="conn-test">
        <button type="button" className="conn-btn" disabled={testing || !canTest} onClick={() => void runTest(scope, kind, host, port)}>
          {testing ? "Testing…" : "Test"}
        </button>
        {mine && (
          <span className="conn-test-result" data-kind={mine.ok ? "ok" : "error"} role="status">
            {testResultText(mine)}
          </span>
        )}
      </div>
    );
  }

  function renderDetail(r: Row) {
    const name = connectionName({ id: r.id, label: r.current.label });
    const on = isOn(r.current);
    const statsOnly = isStatsOnly(r.current);
    const h = health[r.id];
    const dot = statusDot(on, h?.state, connectionLiveness(statsOnly, h?.state, h?.last_packet_at, h?.last_stats_at, livenessSeverity));
    const isMeshtastic = r.current.kind !== "meshcore_tcp";
    const kind = textOf(r.current.kind);
    const hasPosition = itemFields.some((f) => f.group === "meshtastic_only");
    const myMsg = msg && msg.id === r.id ? msg : null;
    const adv = advancedFields(r);
    const port = typeof r.current.port === "number" ? r.current.port : 0;
    return (
      <div className="conn-detail">
        <button type="button" className="conn-back" onClick={backToList}>
          Back To Connections
        </button>
        <h3 className="conn-title">
          {name}
          {statsOnly && <span className="conn-pill">Stats Only</span>}
        </h3>

        <div className="conn-status-row">
          <span className={`conn-dot conn-dot-${dot.color}`} title={dot.label} aria-hidden="true" />
          <span className="conn-status-text" aria-live="polite">
            {statusLine(
              {
                enabled: on,
                state: h?.state,
                lastPacketAt: h?.last_packet_at,
                lastError: h?.last_error,
                lastErrorAt: h?.last_error_at,
                lastConnectedAt: h?.last_connected_at,
                reconnectAttempts: h?.reconnect_attempts,
                nextRetryAt: h?.next_retry_at,
                statsOnly,
                lastStatsAt: h?.last_stats_at,
              },
              now,
            )}
          </span>
          <div className="conn-onoff-wrap">
            <div className="theme-toggle conn-onoff" role="group" aria-label="Connection power">
              <button type="button" data-active={on ? "true" : "false"} aria-pressed={on} disabled={busy} onClick={() => void onToggle(true)}>
                On
              </button>
              <button type="button" data-active={on ? "false" : "true"} aria-pressed={!on} disabled={busy} onClick={() => void onToggle(false)}>
                Off
              </button>
            </div>
            <span className="conn-help">Applies immediately</span>
          </div>
        </div>

        <div className="conn-field">
          <span className="conn-label">{textFor("label").label}</span>
          <input type="text" aria-label="Name" value={textOf(r.current.label)} onChange={(e) => setField(r.id, "label", e.target.value)} />
          <span className="conn-help">{textFor("label").help}</span>
        </div>

        <div className="conn-field">
          <span className="conn-label">{textFor("kind").label}</span>
          <div className="conn-readonly" aria-label="Radio Type">
            {CONNECTION_KIND_LABEL[kind] ?? (kind || "Unknown type")}
          </div>
          <span className="conn-help">Remove and add again to change the type.</span>
        </div>

        <div className="conn-address">
          <div className="conn-field">
            <span className="conn-label">{textFor("host").label}</span>
            <input type="text" aria-label="Host" value={textOf(r.current.host)} onChange={(e) => setField(r.id, "host", e.target.value)} />
          </div>
          <div className="conn-field">
            <span className="conn-label">{textFor("port").label}</span>
            <NumBox value={r.current.port} ariaLabel="Port" step={1} onChange={(n) => setField(r.id, "port", n === null ? 0 : Math.trunc(n))} />
          </div>
        </div>
        <div className="conn-address-help">
          <span className="conn-help">{textFor("host").help}</span>
          <span className="conn-help">{portHelp(kind)}</span>
        </div>
        {renderTest(r.id, kind, textOf(r.current.host), port)}

        <fieldset className="conn-trust">
          <legend className="conn-label">{textFor("trust_tier").label}</legend>
          {showsTrustPicker(kind) ? (
            CONNECTION_TRUST_TIERS.map((tier) => (
              <label key={tier.value} className="conn-trust-option">
                <input
                  type="radio"
                  name={`trust-${r.id}`}
                  checked={r.current.trust_tier === tier.value}
                  onChange={() => setField(r.id, "trust_tier", tier.value)}
                />
                <span className="conn-trust-text">
                  <span>{tier.label}</span>
                  <span className="conn-help">{trustTierHelp(tier.value, kind)}</span>
                </span>
              </label>
            ))
          ) : (
            <span className="conn-help">{META_TRUST_SENTENCE}</span>
          )}
          <div className="conn-field conn-store">
            <label className="conn-check">
              <input type="checkbox" checked={!statsOnly} onChange={(e) => setField(r.id, "ingest_packets", e.target.checked)} />
              <span>{textFor("ingest_packets").label}</span>
            </label>
            <span className="conn-help">{textFor("ingest_packets").help}</span>
          </div>
        </fieldset>

        {hasPosition && isMeshtastic && (
          <div className="conn-field">
            <span className="conn-label">{textFor("station_position").label}</span>
            <div className="conn-position">
              <div className="conn-pos-field">
                <span className="conn-label">{textFor("station_lat").label}</span>
                <NumBox value={r.current.station_lat} allowNull ariaLabel="Latitude" onChange={(n) => setField(r.id, "station_lat", n)} />
              </div>
              <div className="conn-pos-field">
                <span className="conn-label">{textFor("station_lon").label}</span>
                <NumBox value={r.current.station_lon} allowNull ariaLabel="Longitude" onChange={(n) => setField(r.id, "station_lon", n)} />
              </div>
              <button
                type="button"
                className="conn-btn"
                onClick={() => {
                  setField(r.id, "station_lat", null);
                  setField(r.id, "station_lon", null);
                }}
              >
                Clear
              </button>
            </div>
            <span className="conn-help">{textFor("station_position").help}</span>
          </div>
        )}

        {adv.length > 0 && (
          <details className="conn-advanced">
            <summary>Advanced</summary>
            <div className="conn-advanced-body">{adv.map((f) => renderAdvanced(f, r))}</div>
          </details>
        )}

        <div className="conn-actions">
          {myMsg && (
            <span className="settings-save-status conn-result" data-kind={myMsg.kind} role="status">
              {myMsg.text}
            </span>
          )}
          {saveDuplicate && (
            <span className="settings-save-status conn-result" data-kind="error" role="alert">
              Another connection already uses this host and port: {saveDuplicate}
            </span>
          )}
          <button type="button" className="settings-save-btn" disabled={busy || !dirty} onClick={() => void onSave(!!saveDuplicate)}>
            {busy ? "Saving…" : saveDuplicate ? "Save Anyway" : "Save"}
          </button>
          {saveDuplicate && (
            <button type="button" className="conn-btn" onClick={() => setSaveDuplicate(null)}>
              Cancel
            </button>
          )}
          {dirty && <span className="conn-unsaved">Unsaved changes</span>}
          <button type="button" className="settings-record-remove-btn" disabled={busy} onClick={() => void onRemoveClick()}>
            Remove
          </button>
        </div>

        <div className="conn-footer">Internal ID: {r.id} (fixed)</div>
      </div>
    );
  }

  function renderAdd() {
    const portNum = Number(addPort);
    return (
      <div className="conn-detail conn-add">
        <button type="button" className="conn-back" onClick={() => setScreen("list")}>
          Back To Connections
        </button>
        <h3 className="conn-title">Add Connection</h3>
        <div className="conn-field">
          <span className="conn-label">Name</span>
          <input
            type="text"
            aria-label="Name"
            value={addName}
            onChange={(e) => {
              setAddName(e.target.value);
              setAddDuplicate(null);
            }}
            autoFocus
          />
          <span className="conn-help">{CONNECTION_FIELD_TEXT.label.help}</span>
        </div>
        <div className="conn-field">
          <span className="conn-label">Radio Type</span>
          <select
            aria-label="Radio Type"
            value={addKind}
            onChange={(e) => {
              setAddKind(e.target.value);
              setAddDuplicate(null);
              setTestResult(null);
              if (!addPortTouched) setAddPort(String(defaultPort(e.target.value)));
            }}
          >
            {CONNECTION_KIND_ORDER.map((k) => (
              <option key={k} value={k}>
                {CONNECTION_KIND_LABEL[k]}
              </option>
            ))}
          </select>
          <span className="conn-help">{CONNECTION_FIELD_TEXT.kind.help}</span>
          <span className="conn-help">{defaultTrustText(addKind)}</span>
        </div>
        <div className="conn-address">
          <div className="conn-field">
            <span className="conn-label">Host</span>
            <input
              type="text"
              aria-label="Host"
              placeholder="e.g. 192.168.1.50"
              value={addHost}
              onChange={(e) => {
                setAddHost(e.target.value);
                setAddDuplicate(null);
                setTestResult(null);
              }}
            />
          </div>
          <div className="conn-field">
            <span className="conn-label">Port</span>
            <input
              type="number"
              aria-label="Port"
              value={addPort}
              onChange={(e) => {
                setAddPort(e.target.value);
                setAddPortTouched(true);
                setAddDuplicate(null);
                setTestResult(null);
              }}
            />
          </div>
        </div>
        <div className="conn-address-help">
          <span className="conn-help">{CONNECTION_FIELD_TEXT.host.help}</span>
          <span className="conn-help">{portHelp(addKind)}</span>
        </div>
        {renderTest("add", addKind, addHost, portNum)}
        <div className="conn-actions conn-actions-add">
          {addError && (
            <span className="settings-save-status conn-result" data-kind="error" role="alert">
              {addError}
            </span>
          )}
          {addDuplicate && (
            <span className="settings-save-status conn-result" data-kind="error" role="alert">
              Another connection already uses this host and port: {addDuplicate}
            </span>
          )}
          {addDuplicate ? (
            <button type="button" className="settings-save-btn" disabled={busy} onClick={() => void onAdd(true)}>
              {busy ? "Adding…" : "Save Anyway"}
            </button>
          ) : (
            <button type="button" className="settings-save-btn" disabled={busy} onClick={() => void onAdd(false)}>
              {busy ? "Adding…" : "Add And Connect"}
            </button>
          )}
          <button type="button" className="conn-btn" onClick={() => setScreen("list")}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-pane conn-panel" data-screen={screen}>
      {removeDialog && (
        <ConfirmModal
          title="Remove Connection"
          message={removeDialog.message}
          onCancel={() => setRemoveDialog(null)}
          actions={[
            {
              label: REMOVE_CONNECTION_BUTTON,
              kind: "danger",
              disabled: busy,
              onClick: () => {
                const id = removeDialog.id;
                setRemoveDialog(null);
                void doRemove(id);
              },
            },
            ...(rows.find((r) => r.id === removeDialog.id && isOn(r.current))
              ? [
                  {
                    label: "Turn Off Instead",
                    kind: "outline" as const,
                    disabled: busy,
                    onClick: () => {
                      const id = removeDialog.id;
                      setRemoveDialog(null);
                      void doTurnOffInstead(id);
                    },
                  },
                ]
              : []),
          ]}
        />
      )}
      {leaveDialog && (
        <ConfirmModal
          title="Unsaved Changes"
          message={`Leave ${leaveDialog.name} without saving? Your changes will be lost.`}
          cancelLabel="Keep Editing"
          onCancel={() => setLeaveDialog(null)}
          actions={[
            {
              label: "Discard Changes",
              kind: "danger",
              onClick: () => {
                const d = leaveDialog;
                setLeaveDialog(null);
                d.next();
              },
            },
          ]}
        />
      )}
      {description && <p className="settings-pane-description conn-description">{description}</p>}
      {msg && msg.id === "" && (
        <div className="settings-save-status conn-notice" data-kind={msg.kind} role="status">
          {msg.text}
        </div>
      )}
      <div className="conn-split">
        <div className="conn-list-col">
          <div className="conn-list-tools">
            {showFilter && (
              <input
                type="text"
                className="settings-record-filter"
                placeholder="Filter connections…"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                aria-label="Filter connections"
              />
            )}
            <button type="button" className="settings-record-add-btn" onClick={openAdd}>
              Add Connection
            </button>
          </div>
          <div className="conn-list" role="list">
            {rows.length === 0 && <div className="settings-record-empty">No connections yet. Add one to get started.</div>}
            {rows.length > 0 && visibleIds.length === 0 && <div className="settings-record-empty">No connections match.</div>}
            {visibleIds.map((id) => {
              const r = rows.find((x) => x.id === id)!;
              const on = isOn(r.current);
              const h = health[id];
              const dot = statusDot(on, h?.state, connectionLiveness(isStatsOnly(r.current), h?.state, h?.last_packet_at, h?.last_stats_at, livenessSeverity));
              const kind = CONNECTION_KIND_LABEL[textOf(r.current.kind)] ?? (textOf(r.current.kind) || "Unknown type");
              const fullName = connectionName({ id, label: r.current.label });
              return (
                <button
                  key={id}
                  type="button"
                  role="listitem"
                  className="conn-row"
                  data-active={id === selectedId && screen !== "add" ? "true" : "false"}
                  onClick={() => selectRow(id)}
                >
                  <span className="conn-row-main">
                    <span className={`conn-dot conn-dot-${dot.color}`} title={dot.label} aria-label={dot.label} />
                    <span className="conn-row-name" title={fullName}>
                      {fullName}
                    </span>
                  </span>
                  <span className="conn-row-meta">
                    <span>{kind}</span>
                    <span>{addressText(r.current.host, r.current.port)}</span>
                    <span>{on ? lastHeardText(h?.last_packet_at, now) : "Off"}</span>
                    {isStatsOnly(r.current) && <span>Stats Only</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="conn-detail-col">
          {screen === "add" ? renderAdd() : selected ? renderDetail(selected) : <div className="settings-record-empty">No connection selected. Add one to get started.</div>}
        </div>
      </div>
    </div>
  );
}
